import React, { useEffect, useState, useMemo } from 'react';
import { View, ActivityIndicator, Text, StyleSheet } from 'react-native';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ItineraryEditor from '@cubemeet/module-travel';
import { supabase } from '@cubemeet/core/services/supabase';
import BottomNavPortal from '../components/BottomNavPortal';

/**
 * TripDetailPage - 行程查看/分享页面
 * 路由：/trip/:groupId
 */
const TripDetailPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduxUser = useSelector((s: any) => s?.auth?.user);
  const currentUserId = reduxUser?.id;
  const reduxAuth = useSelector((s: any) => (s as any)?.auth ?? {});
  const reduxAuthLoading = reduxAuth?.loading;
  const reduxIsAuthenticated = Boolean(reduxAuth?.isAuthenticated);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupData, setGroupData] = useState<any>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [itinerary, setItinerary] = useState<any>(null);
  const [creator, setCreator] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const selectedDayIndex = useMemo(() => {
    const day = parseInt(searchParams.get('day') || '1', 10);
    return Math.max(0, day - 1);
  }, [searchParams]);

  const initialShowRouteMap = useMemo(() => {
    return searchParams.get('view') === 'map';
  }, [searchParams]);


  useEffect(() => {
    let mounted = true;

    const decide = async () => {
      try {
        // 行程分享页面应该公开可访问，不需要等待 Redux 认证完成
        // 直接设置 authChecked 为 true，允许页面加载
        
        // If global auth bootstrap (Redux) is still loading, we still allow access
        // Just check if authenticated for optional features
        if (reduxIsAuthenticated) {
          if (!mounted) return;
          setIsAuthenticated(true);
        } else {
          if (!mounted) return;
          setIsAuthenticated(false);
        }
        
        if (!mounted) return;
        setAuthChecked(true);
      } catch (err) {
        if (!mounted) return;
        setIsAuthenticated(false);
        setAuthChecked(true);
      }
    };

    // Run immediately and whenever redux auth loading/auth state changes
    decide();

    return () => { mounted = false; };
  }, [navigate, reduxAuthLoading, reduxIsAuthenticated]);

  useEffect(() => {
    if (!authChecked) return;
    // 移除登录检查，允许公开访问
    // if (!isAuthenticated) return; // navigation triggered in auth effect
    if (!groupId) {
      setError('缺少行程 ID');
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadData = async () => {
      try {
        console.log('[TripDetailPage] Loading data for groupId:', groupId);
        
        // 1. 查询 groups 表
        const { data: groupRes, error: groupErr } = await supabase
          .from('groups')
          .select('*')
          .eq('id', groupId)
          .single();

        console.log('[TripDetailPage] Groups query result:', { groupRes, groupErr });

        if (!mounted) return;

        if (groupErr || !groupRes) {
          throw new Error(groupErr?.message || '行程不存在');
        }

        // 1.5. 查询创建人信息
        let creatorInfo = null;
        if (groupRes.creator_id) {
          const { data: profileRes, error: profileErr } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url, bio')
            .eq('id', groupRes.creator_id)
            .single();
          console.log('[TripDetailPage] Profile query result:', { profileRes, profileErr });
          if (profileRes) {
            creatorInfo = profileRes;
          }
        }

        // 2. 查询所有条目（包括关联的卡片数据和创建者信息）
        const { data: entriesRes, error: entriesErr } = await supabase
          .from('group_itinerary_entries')
          .select(`
            *,
            card:card_id (*),
            creator_profile:creator_id (id, display_name, avatar_url)
          `)
          .eq('group_id', groupId)
          .order('day_number', { ascending: true })
          .order('sort_order', { ascending: true });

        console.log('[TripDetailPage] Entries query result:', { entriesRes, entriesErr, count: entriesRes?.length });

        if (!mounted) return;

        if (entriesErr) {
          throw new Error(entriesErr.message);
        }

        // 3. 构建 ItineraryEditor 格式的数据
        const days = [];
        const entriesByDay = new Map<number, any[]>();

        // 计算天数
        let maxDay = 1;
        if (groupRes.start_date && groupRes.end_date) {
          const start = new Date(groupRes.start_date).getTime();
          const end = new Date(groupRes.end_date).getTime();
          maxDay = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
        }

        // 按天数分组条目
        (entriesRes || []).forEach((entry) => {
          const dayNum = entry.day_number || 1;
          if (!entriesByDay.has(dayNum)) {
            entriesByDay.set(dayNum, []);
          }
          entriesByDay.get(dayNum)!.push(entry);
        });

        // 计算日期的辅助函数
        const calculateDateForDay = (dayNumber: number): string | undefined => {
          if (!groupRes.start_date) return undefined;
          try {
            const startDate = new Date(groupRes.start_date);
            if (isNaN(startDate.getTime())) return undefined;
            const targetDate = new Date(startDate);
            targetDate.setDate(targetDate.getDate() + (dayNumber - 1));
            const pad = (n: number) => String(n).padStart(2, '0');
            return `${targetDate.getFullYear()}-${pad(targetDate.getMonth() + 1)}-${pad(targetDate.getDate())}`;
          } catch (e) {
            return undefined;
          }
        };

        // 为每一天创建 Day 对象
        for (let i = 1; i <= maxDay; i++) {
          const dayEntries = entriesByDay.get(i) || [];
          
          // 先将所有条目转换为统一格式
          const allItems = dayEntries.map((entry) => {
            // 格式化时间：只保留 HH:MM，移除秒数
            let formattedTime = entry.start_time;
            if (formattedTime && formattedTime.length > 5) {
              formattedTime = formattedTime.slice(0, 5); // 只取前5个字符 "HH:MM"
            }
            
            return {
              id: entry.id,
              type: entry.card?.card_type || entry.item_type || 'poi',
              title: entry.card?.title || entry.note || '条目',
              time: formattedTime,
              note: entry.note,
              payload: {
                ...(entry.inline_payload || {}),
                ...(entry.custom_payload || {}),
              },
              card: entry.card,
              cardTags: entry.card?.tags || [],
              // 🔥 关键：保留 parent_entry_id 字段
              parent_entry_id: entry.parent_entry_id,
              parentEntryId: entry.parent_entry_id,
              // 保留创建者信息
              creator_id: entry.creator_id,
              creator_profile: entry.creator_profile,
              creator_avatar_url: entry.creator_profile?.avatar_url || null,
              creator_name: entry.creator_profile?.display_name || null,
              created_at: entry.created_at,
              updated_at: entry.updated_at,
              meta: {
                itineraryEntryId: entry.id,
                itemType: entry.item_type,
                parentEntryId: entry.parent_entry_id,
                parent_entry_id: entry.parent_entry_id,
                childSort: entry.child_sort,
                creatorId: entry.creator_id,
                creatorProfile: entry.creator_profile,
              },
            };
          });

          // 分离顶层项目和子项目
          const topLevelItems: any[] = [];
          const childItemsMap = new Map<string, any[]>(); // parentId -> children[]

          allItems.forEach((item) => {
            const parentId = item.parent_entry_id || item.parentEntryId;
            if (parentId) {
              // 这是子项目，添加到 map 中
              if (!childItemsMap.has(parentId)) {
                childItemsMap.set(parentId, []);
              }
              childItemsMap.get(parentId)!.push(item);
            } else {
              // 这是顶层项目
              topLevelItems.push(item);
            }
          });

          // 将子项目附加到父项目的 children 数组
          const items = topLevelItems.map((item) => {
            const children = childItemsMap.get(item.id);
            if (children && children.length > 0) {
              // 按 child_sort 排序
              children.sort((a, b) => {
                const sortA = a.meta?.childSort ?? 0;
                const sortB = b.meta?.childSort ?? 0;
                return sortA - sortB;
              });
              return {
                ...item,
                children,
              };
            }
            return item;
          });

          days.push({
            day: i,
            date: calculateDateForDay(i),
            items,
          });
        }

        // 构建位置字符串
        const locationField = groupRes.destination_city || groupRes.location;
        let locationStr = '';
        if (typeof locationField === 'string') {
          locationStr = locationField;
        } else if (locationField && typeof locationField === 'object') {
          const { city, district, province, country } = locationField;
          locationStr = [city, district, province, country].filter(Boolean).join(', ');
        }

        // 构建最终的 itinerary 对象
        const finalItinerary = {
          title: groupRes.name || '我的行程',
          location: locationStr,
          days,
          start_date: groupRes.start_date,
          end_date: groupRes.end_date,
        };

        setGroupData(groupRes);
        setCreator(creatorInfo);
        console.log('[TripDetailPage] Creator info set:', creatorInfo);
        console.log('[TripDetailPage] About to render ItineraryEditor with:', { 
          showShareButton: true,
          contentType: 'trip',
          contentId: groupId,
          creator: creatorInfo
        });
        setItinerary(finalItinerary);
        setLoading(false);
      } catch (e: any) {
        if (!mounted) return;
        setError(e.message || '加载失败');
        setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [groupId, authChecked]); // 添加 authChecked 到依赖数组

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color="#FF6B35" />
        <Text style={styles.loadingText}>加载行程中...</Text>
      </View>
    );
  }

  if (error || !itinerary) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 16 }}>
        <Text style={styles.errorText}>{error || '加载失败'}</Text>
      </View>
    );
  }

  return (
    <>
      <View style={styles.container}>
        <ItineraryEditor
          initialItinerary={itinerary}
          initialFormData={groupData}
          initialSelectedDayIndex={selectedDayIndex}
          onDaySelect={(dayIndex) => {
            navigate(`/trip/${groupId}?day=${dayIndex + 1}`);
          }}
          onBack={() => navigate('/travel')}
          supabaseClient={supabase}
          currentUserId={currentUserId}
          showLocationBadge={false}
          // ShareButton props
          contentType="trip"
          contentId={groupId}
          creator={creator}
          showShareButton={true}
          initialShowRouteMap={initialShowRouteMap}
        />
      </View>
      <BottomNavPortal activeIndex={2} disableAutoPadding />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingText: {
    marginTop: 12,
    color: '#6B7280',
  },
  errorText: {
    fontSize: 16,
    color: '#DC2626',
    fontWeight: '600',
  },
});

export default TripDetailPage;
